import { useMemo } from 'react'
import { useModuleConfig } from '../hooks/useModuleConfig'
import { useMatchScreen } from '../../context/MatchScreenContext'
import type { ModuleKey } from '../moduleRegistry'
import { buildPersonalizationContext } from './buildPersonalizationContext'
import { applyPersonalization } from './applyPersonalization'

/**
 * Drop-in replacement for `useModuleConfig` on the heading and concierge modules.
 * Reads the module config exactly as `useModuleConfig` does, then applies the config's optional
 * `personalization` rules against the context from `buildPersonalizationContext` (match types,
 * asset tier, self-schedule eligibility, funnel answers) -- the same derivation
 * `registerPersonalizationCopy` uses, so the two cannot diverge. With no `personalization`
 * key, no matching rule, or any malformed input, the returned config is identical to today's
 * behavior.
 *
 * The matching copy group is reported to Mixpanel at bootstrap by
 * `registerPersonalizationCopy` (see matchScreen/index.tsx), not here, so the session's first
 * tracked events already carry the `personalization_copy_*` super properties.
 */
export function usePersonalizedModuleConfig<T extends Record<string, unknown>> (
  moduleKey: ModuleKey,
  defaults: T
): T {
  const config = useModuleConfig(moduleKey, defaults)
  const { state } = useMatchScreen()

  return useMemo(() => {
    const context = buildPersonalizationContext({
      matches: state.matches,
      matchedSavings: state.matchedSavings,
      funnelAnswers: state.funnelAnswers,
      selfSchedulingEnabled: state.selfSchedulingEnabled,
      revisit: state.revisit
    })
    return applyPersonalization(config, context).config
  }, [config, state.matches, state.matchedSavings, state.funnelAnswers, state.selfSchedulingEnabled, state.revisit])
}
